import { useEffect, useRef, useState } from 'react';

interface LazyVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  rootMargin?: string;
  threshold?: number;
  enabled?: boolean;
}

/**
 * LazyVideo component that only loads video src when in viewport, Automatically clears src when out of viewport to save memory
 */
export const LazyVideo = ({
  src,
  rootMargin = '200px',
  threshold = 0.1,
  enabled = true,
  ...videoProps
}: LazyVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    if (!enabled) {
      setIsInView(false);
      return;
    }

    const videoElement = videoRef.current;
    if (!videoElement) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          setIsInView(entry.isIntersecting);
        });
      },
      {
        rootMargin,
        threshold,
      },
    );

    observer.observe(videoElement);

    return () => {
      observer.disconnect();
    };
  }, [rootMargin, threshold, enabled]);

  // Conditionally set src: remove when out of view to save memory, set when in view
  return (
    <video ref={videoRef} {...videoProps} src={isInView ? src : undefined} />
  );
};

export default LazyVideo;
